import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Plus, Minus, LogOut, ChefHat, ShoppingBag, Trash2, PlusCircle, Search, KeyRound, CopyPlus, Database, Pencil, Save, X, Bell, BellOff, Eye, CalendarDays, Wand2, ImageOff, Loader2, Clock, ArrowDownNarrowWide, ArrowUpNarrowWide } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Oferta zilei', 'Ciorbe', 'Combo', 'Specialități', 'Vegetarian', 'Paste / Salate', 'Clătite'];

const daysOfWeek = [
  { value: 'luni', label: 'Luni' },
  { value: 'marti', label: 'Marți' },
  { value: 'miercuri', label: 'Miercuri' },
  { value: 'joi', label: 'Joi' },
  { value: 'vineri', label: 'Vineri' }
];

function getDateForDay(dayName) {
  // Explicitly use Romanian timezone — avoids UTC date mismatch between midnight and ~03:00 AM
  const nowRo = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Bucharest' }));
  const currentDay = nowRo.getDay(); // 0=Sun … 6=Sat in RO tz
  const daysMap = { luni: 1, marti: 2, miercuri: 3, joi: 4, vineri: 5 };
  const targetDay = daysMap[dayName];

  let diff = targetDay - currentDay;
  if (diff < 0) diff += 7;

  const targetDate = new Date(nowRo);
  targetDate.setDate(nowRo.getDate() + diff);
  // Use local (RO-tz) date parts — NOT toISOString() which would give the UTC date
  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getCurrentRomaniaDay() {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Bucharest', weekday: 'long' });
  const day = fmt.format(new Date()).toLowerCase();
  const map = { monday: 'luni', tuesday: 'marti', wednesday: 'miercuri', thursday: 'joi', friday: 'vineri' };
  return map[day] || 'luni'; // fallback to luni on weekends
}

function getTodayDateRO() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date());
}

function addDaysToDateStr(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().split('T')[0];
}

function formatOrderDateLabel(dateStr) {
  if (!dateStr) return '—';
  // Strip time component — production orders may store full ISO strings (e.g. "2025-05-25T12:00:00.000Z")
  const dateOnly = typeof dateStr === 'string' && dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  try {
    const today = getTodayDateRO();
    const tomorrow = addDaysToDateStr(today, 1);
    const formatted = new Intl.DateTimeFormat('ro-RO', { day: 'numeric', month: 'short' }).format(new Date(dateOnly + 'T00:00:00'));
    if (dateOnly === today) return `Astăzi (${formatted})`;
    if (dateOnly === tomorrow) return `Mâine (${formatted})`;
    return formatted;
  } catch {
    return dateOnly || '—';
  }
}

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('menu');
  const [selectedDay, setSelectedDay] = useState(getCurrentRomaniaDay);
  const [menuItems, setMenuItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersSearch, setOrdersSearch] = useState('');
  const [orderSortOrder, setOrderSortOrder] = useState('newest'); // 'newest' | 'oldest'
  const [orderDetail, setOrderDetail] = useState(null);
  const [products, setProducts] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newStock, setNewStock] = useState('');
  const [dailyReport, setDailyReport] = useState(null);
  const [autoGenerateImage, setAutoGenerateImage] = useState(false);
  const [generatingImageForId, setGeneratingImageForId] = useState(null); // product id currently being generated
  const [settings, setSettings] = useState({
    iban: '',
    company_name: '',
    important_info: ''
  });
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productsSearch, setProductsSearch] = useState('');
  const [slotConfig, setSlotConfig] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [newOrderAlerts, setNewOrderAlerts] = useState([]); // queue of unacknowledged new orders
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const v = localStorage.getItem('garden8_sound_enabled');
    return v === null ? true : v === 'true';
  });
  const knownOrderIdsRef = useRef(new Set());
  const initialLoadRef = useRef(true);
  const ringIntervalRef = useRef(null);   // setInterval handle for looping chime
  const soundEnabledRef = useRef(true);   // mirror of soundEnabled for stale-closure-safe reads

  // Must be defined before any useEffect so cleanup can call it safely
  const stopRinging = () => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
  };

  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Specialități',
    is_combo: false,
    recurring: false,
    default_stock: '',
    option_groups: []
  });
  const [addonGroups, setAddonGroups] = useState([]);
  const [showAddonDialog, setShowAddonDialog] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newAddon, setNewAddon] = useState({ name: '', base_price: '', category: 'Combo', items: [], active: true });
  const [newAddonItem, setNewAddonItem] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    // Note: fetchMenuItems has its own dedicated effect below (selectedDay-scoped)
    fetchOrders();
    fetchProducts();
    fetchSettings();
    fetchAddonGroups();
    fetchSlotConfig();
    fetchPendingPayments();
    
    const interval = setInterval(() => {
      fetchOrders();
      fetchProducts();
      fetchPendingPayments();
    }, 5000);
    
    return () => { clearInterval(interval); stopRinging(); };
  }, [user, navigate]);

  // Auto-unlock AudioContext on first user interaction — required by iOS Safari & Android Chrome
  useEffect(() => {
    if (audioUnlockedRef.current) return;
    const handler = () => unlockAudio();
    window.addEventListener('click', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    window.addEventListener('touchstart', handler, { once: true, passive: true });
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMenuItems = async () => {
    try {
      const date = getDateForDay(selectedDay);
      const { data } = await axios.get(`${API}/menu?date=${date}`, { withCredentials: true });
      setMenuItems(data);
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  // Dedicated polling for menu items.
  // This effect restarts its interval every time selectedDay changes, so the
  // interval callback always closes over the CURRENT day — no stale closure.
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    fetchMenuItems();
    const iv = setInterval(fetchMenuItems, 5000);
    return () => clearInterval(iv);
  }, [selectedDay, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Keep soundEnabledRef current so interval callbacks (setInterval closures) always read the latest value
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const ensureAudioContext = () => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      audioCtxRef.current = new AudioCtx();
    }
    return audioCtxRef.current;
  };

  const unlockAudio = async () => {
    const ctx = ensureAudioContext();
    if (!ctx) return false;
    try {
      if (ctx.state === 'suspended') await ctx.resume();
      // Play a super-quiet, super-short blip so Chrome/Safari mark the context as "unlocked by user gesture"
      const buffer = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      src.start(0);
      audioUnlockedRef.current = true;
      return true;
    } catch (e) {
      return false;
    }
  };

  const playChime = async (force = false) => {
    if (!force && !soundEnabledRef.current) return;
    const ctx = ensureAudioContext();
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') await ctx.resume();
    } catch (e) { /* ignore */ }
    if (ctx.state !== 'running') return;
    try {
      const now = ctx.currentTime;
      // Kitchen bell: loud fundamental A5 + harmonics with slow exponential decay
      const bell = (freq, peakGain, decayDur) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.001, now);
        g.gain.exponentialRampToValueAtTime(peakGain, now + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, now + decayDur);
        osc.connect(g).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + decayDur + 0.05);
      };
      bell(880,    0.70, 1.4);  // A5 — fundamental (loud)
      bell(1760,   0.45, 1.0);  // A6 — octave overtone
      bell(2637,   0.22, 0.7);  // E7 — 2nd overtone
      bell(1108.73,0.35, 1.1);  // C#6 — adds bell character
    } catch (e) {
      console.error('Chime failed:', e);
    }
  };

  const startRinging = () => {
    if (!soundEnabledRef.current) return;
    if (ringIntervalRef.current) return; // already ringing
    playChime();
    ringIntervalRef.current = setInterval(playChime, 2500);
  };

  const acknowledgeOrder = (order) => {
    setNewOrderAlerts(prev => {
      const remaining = prev.filter(o => o.id !== order.id);
      if (remaining.length === 0) stopRinging();
      return remaining;
    });
    setActiveTab('orders');
    setOrderDetail(order);
  };

  const showBrowserNotification = (order) => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      try {
        new Notification('Comandă nouă — Garden8', {
          body: `${order.customer_name} · ${order.total_amount.toFixed(2)} RON · Ridicare: ${order.pickup_time}`,
          icon: '/favicon.ico',
          tag: `order-${order.id}`,
        });
      } catch (e) {
        // ignore
      }
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`, { withCredentials: true });
      // Detect new orders (skip on first load to avoid alerting for existing)
      if (!initialLoadRef.current) {
        const newlyArrived = data.filter(o => !knownOrderIdsRef.current.has(o.id));
        if (newlyArrived.length > 0) {
          setNewOrderAlerts(prev => [...prev, ...newlyArrived]);
          startRinging();
          showBrowserNotification(newlyArrived[0]);
        }
      }
      knownOrderIdsRef.current = new Set(data.map(o => o.id));
      initialLoadRef.current = false;
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pending-payments`, { withCredentials: true });
      setPendingPayments(data);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    }
  };

  const handleMarkPaid = async (sessionId) => {
    if (!window.confirm('Confirmă că plata pentru această comandă a fost primită. Comanda va fi creată în lista principală.')) return;
    try {
      await axios.post(`${API}/admin/pending-payments/${sessionId}/mark-paid`, {}, { withCredentials: true });
      toast.success('Plata marcată. Comanda a fost creată.');
      await Promise.all([fetchPendingPayments(), fetchOrders()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la marcarea plății');
    }
  };

  const handleDeletePending = async (sessionId) => {
    if (!window.confirm('Ștergi această tranzacție abandonată? Comanda nu va fi creată.')) return;
    try {
      await axios.delete(`${API}/admin/pending-payments/${sessionId}`, { withCredentials: true });
      toast.success('Tranzacție ștearsă');
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  const toggleSound = async () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('garden8_sound_enabled', String(next));
    if (next) {
      const unlocked = await unlockAudio();
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      // Play test chime — force=true bypasses soundEnabled check (state update is async)
      setTimeout(() => playChime(true), 150);
      if (unlocked) {
        toast.success('Alertă sonoră activă — vei fi anunțat la fiecare comandă nouă');
      } else {
        toast.warning('Alertă activată dar audio blocat. Atinge ecranul o dată și reîncearcă.');
      }
    } else {
      stopRinging();
      toast.info('Alertă sonoră dezactivată');
    }
  };

  const fetchProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products${searchQuery ? `?search=${searchQuery}` : ''}`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success('Setări salvate cu succes');
      setSettingsChanged(false);
    } catch (error) {
      toast.error('Eroare la salvarea setărilor');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (user && user.role === 'admin') {
        fetchProducts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const updateStock = async (itemId, newStock) => {
    try {
      await axios.patch(
        `${API}/menu/${itemId}/stock`,
        { stock: newStock },
        { withCredentials: true }
      );
      fetchMenuItems();
    } catch (error) {
      toast.error('Eroare la actualizarea stocului');
    }
  };

  const addProductToMenu = async () => {
    if (!selectedProduct) {
      toast.error('Selectează un produs');
      return;
    }

    try {
      await axios.post(
        `${API}/menu`,
        {
          product_id: selectedProduct.id,
          stock: newStock === '' || newStock === null ? null : parseInt(newStock),
          date: getDateForDay(selectedDay)
        },
        { withCredentials: true }
      );
      toast.success('Produs adăugat în meniu');
      setShowAddDialog(false);
      setSelectedProduct(null);
      setNewStock('');
      fetchMenuItems();
    } catch (error) {
      toast.error('Eroare la adăugarea produsului');
    }
  };

  const createProduct = async () => {
    try {
      const priceNum = parseFloat(newProduct.price);
      if (!newProduct.name || newProduct.name.trim() === '') {
        toast.error('Numele produsului este obligatoriu');
        return;
      }
      if (Number.isNaN(priceNum) || priceNum < 0) {
        toast.error('Prețul trebuie să fie un număr valid (ex: 25 sau 25.50)');
        return;
      }
      const cleanGroups = (newProduct.option_groups || [])
        .filter(g => g && g.title && (g.options || []).some(o => o.name))
        .map(g => ({
          title: g.title,
          required: g.required !== false,
          multi: !!g.multi,
          options: (g.options || []).filter(o => o.name).map(o => ({
            name: o.name,
            extra_price: parseFloat(o.extra_price) || 0
          }))
        }));
      const stockRaw = newProduct.default_stock;
      const stockNum = stockRaw === '' || stockRaw === null || stockRaw === undefined ? null : parseInt(stockRaw);
      const payload = {
        name: newProduct.name.trim(),
        description: (newProduct.description || '').trim(),
        price: priceNum,
        category: newProduct.category,
        is_combo: !!newProduct.is_combo,
        recurring: !!newProduct.recurring,
        default_stock: Number.isNaN(stockNum) ? null : stockNum,
        option_groups: cleanGroups,
      };
      if (newProduct._editingId) {
        await axios.put(`${API}/products/${newProduct._editingId}`, payload, { withCredentials: true });
        toast.success('Produs actualizat');
        if (autoGenerateImage) {
          setGeneratingImageForId(newProduct._editingId);
          axios.post(`${API}/products/${newProduct._editingId}/generate-image`, {}, { withCredentials: true })
            .then(() => { toast.info('Ilustrație generată cu succes!'); setGeneratingImageForId(null); fetchProducts(); })
            .catch(() => { toast.error('Eroare la generarea imaginii'); setGeneratingImageForId(null); });
        }
      } else {
        const { data: created } = await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success('Produs nou creat');
        if (autoGenerateImage && created?.id) {
          setGeneratingImageForId(created.id);
          toast.info('Se generează ilustrația... (30-60 secunde)');
          axios.post(`${API}/products/${created.id}/generate-image`, {}, { withCredentials: true })
            .then(() => { toast.success('Ilustrație generată!'); setGeneratingImageForId(null); fetchProducts(); })
            .catch(() => { toast.error('Eroare la generarea imaginii'); setGeneratingImageForId(null); });
        }
      }
      setShowProductDialog(false);
      setAutoGenerateImage(false);
      setNewProduct({ name: '', description: '', price: '', category: 'Specialități', is_combo: false, recurring: false, default_stock: '', option_groups: [] });
      fetchProducts();
    } catch (error) {
      let msg = 'Eroare la salvarea produsului';
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        msg = detail.map(d => `${d.loc?.slice(-1)[0] || ''}: ${d.msg}`).join('; ');
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (error?.response?.status) {
        msg = `Eroare HTTP ${error.response.status} la salvare produs`;
      }
      toast.error(msg);
      console.error('Product save error:', error?.response?.data || error);
    }
  };

  const openEditProductDialog = (product) => {
    setNewProduct({
      _editingId: product.id,
      _hasImage: !!product.image_storage_path,
      _imageGenerating: !!product.image_generating,
      name: product.name || '',
      description: product.description || '',
      price: product.price ?? '',
      category: product.category || 'Specialități',
      is_combo: !!product.is_combo,
      recurring: !!product.recurring,
      default_stock: product.default_stock ?? '',
      option_groups: (product.option_groups || []).map(g => ({
        title: g.title || '',
        required: g.required !== false,
        multi: !!g.multi,
        options: (g.options || []).map(o => ({ name: o.name, extra_price: o.extra_price ?? 0 }))
      }))
    });
    setAutoGenerateImage(false);
    setShowProductDialog(true);
  };

  const fetchAddonGroups = async () => {
    try {
      const { data } = await axios.get(`${API}/addon-groups`);
      setAddonGroups(data);
    } catch (e) {
      console.error('Error fetching addon groups', e);
    }
  };

  const fetchSlotConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/pickup-slots/config`, { withCredentials: true });
      setSlotConfig(data);
    } catch (e) {
      console.error('Error fetching slot config', e);
    }
  };

  const updateSlotCapacity = async (slot, capacity) => {
    try {
      await axios.put(`${API}/pickup-slots/config`, { slot, capacity: parseInt(capacity) || 0 }, { withCredentials: true });
      setSlotConfig(prev => prev.map(s => s.slot === slot ? { ...s, capacity: parseInt(capacity) || 0 } : s));
    } catch (e) {
      toast.error('Eroare la salvarea capacității');
    }
  };

  const saveAddonGroup = async () => {
    try {
      const payload = {
        name: newAddon.name,
        base_price: parseFloat(newAddon.base_price) || 0,
        category: newAddon.category,
        items: (newAddon.items || []).filter(i => i && i.trim()),
        active: !!newAddon.active,
      };
      if (editingAddon) {
        await axios.put(`${API}/addon-groups/${editingAddon.id}`, payload, { withCredentials: true });
        toast.success('Grup suplimente actualizat');
      } else {
        await axios.post(`${API}/addon-groups`, payload, { withCredentials: true });
        toast.success('Grup suplimente creat');
      }
      setShowAddonDialog(false);
      setEditingAddon(null);
      setNewAddon({ name: '', base_price: '', category: 'Combo', items: [], active: true });
      setNewAddonItem('');
      fetchAddonGroups();
    } catch (e) {
      toast.error('Eroare la salvare');
    }
  };

  const deleteAddonGroup = async (id) => {
    if (!window.confirm('Sigur ștergi acest grup de suplimente?')) return;
    try {
      await axios.delete(`${API}/addon-groups/${id}`, { withCredentials: true });
      toast.success('Grup șters');
      fetchAddonGroups();
    } catch (e) {
      toast.error('Eroare la ștergere');
    }
  };

  const updateProduct = async () => {
    if (!editingProduct) return;
    try {
      await axios.put(
        `${API}/products/${editingProduct.id}`,
        {
          name: editingProduct.name,
          description: editingProduct.description || '',
          price: parseFloat(editingProduct.price),
          category: editingProduct.category
        },
        { withCredentials: true }
      );
      toast.success('Produs actualizat');
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error('Eroare la actualizarea produsului');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Sigur ștergi acest produs din baza de date? Această acțiune este permanentă.')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success('Produs șters din baza de date');
      fetchProducts();
    } catch (error) {
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('Sigur dore ti să ștergi acest produs?')) return;
    
    try {
      await axios.delete(`${API}/menu/${itemId}`, { withCredentials: true });
      toast.success('Produs șters');
      fetchMenuItems();
    } catch (error) {
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const duplicateMenuFromYesterday = async () => {
    try {
      setDuplicateLoading(true);
      const targetDate = getDateForDay(selectedDay);
      const sourceDate = addDaysToDateStr(targetDate, selectedDay === 'luni' ? -3 : -1);

      const { data } = await axios.post(
        `${API}/menu/duplicate`,
        { source_date: sourceDate, target_date: targetDate, reset_stock: false },
        { withCredentials: true }
      );
      if (data.length === 0) {
        toast.info('Toate produsele din ziua precedentă sunt deja în meniu');
      } else {
        toast.success(`${data.length} produse duplicate din ziua precedentă`);
      }
      fetchMenuItems();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Eroare la duplicare';
      toast.error(msg);
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Parolele noi nu coincid');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Parola nouă trebuie să aibă minim 8 caractere');
      return;
    }
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: passwordForm.current_password, new_password: passwordForm.new_password },
        { withCredentials: true }
      );
      toast.success('Parola a fost schimbată cu succes');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Eroare la schimbarea parolei';
      toast.error(msg);
    }
  };

  const updateOrderStatus = async (orderId, status, delayMinutes = null) => {
    try {
      const body = { status };
      if (delayMinutes !== null) body.delay_minutes = delayMinutes;
      await axios.patch(
        `${API}/orders/${orderId}/status`,
        body,
        { withCredentials: true }
      );
      fetchOrders();
    } catch (error) {
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const applyDelay = async (orderId) => {
    const raw = window.prompt('Câte minute de întârziere anunți clientului?', '15');
    if (raw === null) return;
    const mins = parseInt(raw);
    if (Number.isNaN(mins) || mins <= 0) {
      toast.error('Numărul de minute trebuie să fie mai mare decât 0');
      return;
    }
    await updateOrderStatus(orderId, 'intarziere', mins);
    toast.success(`Întârziere de ${mins} min anunțată clientului`);
  };

  const generateDailyReport = () => {
    const date = getDateForDay(selectedDay);
    const dayOrders = orders.filter(o => o.order_date === date);
    
    const totalRevenue = dayOrders.reduce((sum, order) => sum + order.total_amount, 0);
    const totalOrders = dayOrders.length;
    
    const itemsSold = {};
    const revenueByCategory = {};
    const ordersByPaymentMethod = {
      'Plată la ridicare': 0,
      'Transfer Bancar': 0
    };
    
    dayOrders.forEach(order => {
      ordersByPaymentMethod[order.payment_method] = (ordersByPaymentMethod[order.payment_method] || 0) + 1;
      
      order.items.forEach(item => {
        if (!itemsSold[item.name]) {
          itemsSold[item.name] = { quantity: 0, revenue: 0, category: '' };
        }
        itemsSold[item.name].quantity += item.quantity;
        itemsSold[item.name].revenue += item.price * item.quantity;
        
        const menuItem = menuItems.find(mi => mi.name === item.name);
        if (menuItem) {
          itemsSold[item.name].category = menuItem.category;
          if (!revenueByCategory[menuItem.category]) {
            revenueByCategory[menuItem.category] = 0;
          }
          revenueByCategory[menuItem.category] += item.price * item.quantity;
        }
      });
    });
    
    const stockStatus = menuItems.map(item => ({
      name: item.name,
      category: item.category,
      initialStock: item.stock + (itemsSold[item.name]?.quantity || 0),
      sold: itemsSold[item.name]?.quantity || 0,
      remaining: item.stock
    }));
    
    setDailyReport({
      date,
      totalRevenue,
      totalOrders,
      itemsSold: Object.entries(itemsSold).map(([name, data]) => ({ name, ...data })),
      revenueByCategory,
      ordersByPaymentMethod,
      stockStatus
    });
  };

  useEffect(() => {
    if (activeTab === 'reports' && orders.length > 0 && menuItems.length > 0) {
      generateDailyReport();
    }
  }, [activeTab, selectedDay, orders, menuItems]);

  return (
    <div className="min-h-screen bg-[#E8E4DD]">

      {/* ── New Order Alert Modal (z-[200] — always on top) ── */}
      {newOrderAlerts.length > 0 && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="new-order-alert-overlay"
        >
          {/* Dark backdrop — intentionally NOT dismissable on click */}
          <div className="absolute inset-0 bg-black/70" />

          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full border-4 border-red-500 overflow-hidden"
            data-testid="new-order-alert-modal"
          >
            {/* Header bar */}
            <div className="bg-red-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-7 h-7 text-white animate-bounce" />
                <span className="text-white text-xl font-bold tracking-wide">COMANDĂ NOUĂ!</span>
              </div>
              {newOrderAlerts.length > 1 && (
                <span className="bg-white text-red-600 font-bold text-sm px-2.5 py-1 rounded-full">
                  +{newOrderAlerts.length - 1}
                </span>
              )}
            </div>

            {/* Order preview */}
            {(() => {
              const order = newOrderAlerts[0];
              return (
                <div className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-xl font-bold text-[#1F1F1F]">{order.customer_name}</p>
                      <p className="text-sm text-[#999] font-mono mt-0.5">#{order.id.slice(-8).toUpperCase()}</p>
                    </div>
                    <span className="text-2xl font-bold text-[#7BA05B] shrink-0 ml-4">
                      {order.total_amount.toFixed(2)} RON
                    </span>
                  </div>

                  <div className="bg-[#F8F8F8] rounded-xl p-3 mb-3 space-y-1.5">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-[#2D3748]">
                        <span className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center text-xs font-bold text-[#5C8945] shrink-0">
                          {item.quantity}
                        </span>
                        <span className="font-medium">{item.name}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {order.pickup_time && (
                      <span className="inline-flex items-center gap-1.5 text-sm bg-[#EBF5EB] text-[#2D6A2D] px-3 py-1.5 rounded-full font-semibold">
                        <Clock className="w-3.5 h-3.5" />
                        {order.pickup_time}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-sm bg-[#FFF3E0] text-[#8B4513] px-3 py-1.5 rounded-full font-semibold">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {formatOrderDateLabel(order.order_date)}
                    </span>
                  </div>

                  <button
                    data-testid="acknowledge-order-button"
                    onClick={() => acknowledgeOrder(order)}
                    className="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white py-3.5 rounded-xl text-base font-bold transition-colors"
                  >
                    Confirmă &amp; Deschide Comanda
                  </button>

                  {newOrderAlerts.length > 1 && (
                    <button
                      data-testid="dismiss-all-alerts-button"
                      onClick={() => { setNewOrderAlerts([]); stopRinging(); }}
                      className="w-full mt-2 text-sm text-[#999] hover:text-[#666] py-2 transition-colors"
                    >
                      Ignoră toate ({newOrderAlerts.length}) comenzile noi
                    </button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <header className="sticky top-0 z-50 bg-white border-b border-[#E8E8E8]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-semibold text-[#1F1F1F]">
              Admin Dashboard
            </h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#666]">{user?.email}</span>
              <Button
                data-testid="toggle-sound-alert"
                onClick={toggleSound}
                variant="outline"
                className={`rounded-lg ${soundEnabled ? 'border-[#7BA05B] text-[#5C8945]' : 'border-[#E8E8E8]'}`}
                title={soundEnabled ? 'Alertă comenzi ACTIVĂ' : 'Alertă comenzi OPRITĂ'}
              >
                {soundEnabled ? <Bell className="w-4 h-4 mr-2" /> : <BellOff className="w-4 h-4 mr-2" />}
                {soundEnabled ? 'Alertă ON' : 'Alertă OFF'}
              </Button>
              <Button
                data-testid="change-password-button"
                onClick={() => setShowPasswordDialog(true)}
                variant="outline"
                className="rounded-lg border-[#E8E8E8] hover:bg-[#F8F8F8]"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Schimbă parola
              </Button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                className="rounded-lg border-[#E8E8E8] hover:bg-[#F8F8F8]"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Deconectează
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-3 mb-6">
          <button
            data-testid="menu-tab"
            onClick={() => setActiveTab('menu')}
            className={`category-tab ${activeTab === 'menu' ? 'active' : ''}`}
          >
            <ChefHat className="w-4 h-4 mr-2 inline" />
            Gestionare Meniu
          </button>
          <button
            data-testid="products-tab"
            onClick={() => setActiveTab('products')}
            className={`category-tab ${activeTab === 'products' ? 'active' : ''}`}
          >
            <Database className="w-4 h-4 mr-2 inline" />
            Produse ({products.length})
          </button>
          <button
            data-testid="orders-tab"
            onClick={() => { setActiveTab('orders'); fetchOrders(); }}
            className={`category-tab ${activeTab === 'orders' ? 'active' : ''}`}
          >
            <ShoppingBag className="w-4 h-4 mr-2 inline" />
            Comenzi ({orders.length})
          </button>
          <button
            data-testid="reports-tab"
            onClick={() => setActiveTab('reports')}
            className={`category-tab ${activeTab === 'reports' ? 'active' : ''}`}
          >
            📊 Rapoarte
          </button>
          <button
            data-testid="settings-tab"
            onClick={() => setActiveTab('settings')}
            className={`category-tab ${activeTab === 'settings' ? 'active' : ''}`}
          >
            ⚙️ Setări
          </button>
          {pendingPayments.length > 0 && (
            <button
              data-testid="pending-payments-tab"
              onClick={() => setActiveTab('pending')}
              className={`category-tab ${activeTab === 'pending' ? 'active' : ''}`}
              style={{ backgroundColor: activeTab === 'pending' ? '' : '#FEF3C7', color: activeTab === 'pending' ? '' : '#92400E' }}
            >
              ⏳ Plăți în așteptare ({pendingPayments.length})
            </button>
          )}
        </div>

        {activeTab === 'menu' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <div className="flex gap-2">
                {daysOfWeek.map((day) => (
                  <button
                    key={day.value}
                    data-testid={`admin-day-tab-${day.value}`}
                    onClick={() => setSelectedDay(day.value)}
                    className={`category-tab ${selectedDay === day.value ? 'active' : ''}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Button
                  data-testid="open-create-product"
                  onClick={() => {
                    setNewProduct({ name: '', description: '', price: '', category: 'Specialități', is_combo: false, recurring: false, default_stock: '', option_groups: [] });
                    setShowProductDialog(true);
                  }}
                  className="rounded-lg bg-[#5C9F5F] hover:bg-[#4A8F4D] text-white"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  Produs Nou
                </Button>


                <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-menu-item-button" className="rounded-lg accent-button">
                      <PlusCircle className="w-4 h-4 mr-2" />
                      Adaugă în Meniu
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Adaugă Produs în Meniu Zilei</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Caută produs</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#999]" />
                          <Input
                            data-testid="product-search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Caută după nume..."
                            className="rounded-lg pl-10"
                          />
                        </div>
                      </div>

                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {products.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => setSelectedProduct(product)}
                            className={`minimalist-card p-3 cursor-pointer ${
                              selectedProduct?.id === product.id ? 'border-[#7BA05B] border-2' : ''
                            }`}
                          >
                            <div className="flex justify-between items-center gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                {/* Thumbnail */}
                                {product.image_storage_path ? (
                                  <img
                                    src={`${API}/product-image/${product.id}`}
                                    alt=""
                                    className="w-10 h-10 rounded-md object-cover shrink-0 border border-[#E8E8E8]"
                                  />
                                ) : product.image_generating ? (
                                  <div className="w-10 h-10 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
                                    <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-md bg-[#F5F5F0] flex items-center justify-center shrink-0 border border-[#E8E8E8]">
                                    <ImageOff className="w-4 h-4 text-[#CBD5E0]" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="font-medium text-[#1F1F1F] truncate">{product.name}</p>
                                  <p className="text-sm text-[#666]">{product.category}</p>
                                </div>
                              </div>
                              <p className="font-semibold text-[#7BA05B] shrink-0">{product.price.toFixed(2)} RON</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {selectedProduct && (
                        <div>
                          <Label>Stoc inițial (opțional)</Label>
                          <Input
                            type="number"
                            value={newStock}
                            onChange={(e) => setNewStock(e.target.value)}
                            placeholder="Lasă gol = stoc toată ziua"
                            className="rounded-lg"
                          />
                          <p className="text-xs text-[#999] mt-1">Lasă gol dacă vrei stoc nelimitat (toată ziua)</p>
                        </div>
                      )}

                      <Button
                        data-testid="add-to-menu-button"
                        onClick={addProductToMenu}
                        className="w-full accent-button"
                        disabled={!selectedProduct}
                      >
                        Adaugă în Meniu
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  data-testid="duplicate-menu-button"
                  onClick={duplicateMenuFromYesterday}
                  disabled={duplicateLoading}
                  variant="outline"
                  className="rounded-lg border-[#7BA05B] text-[#5C8945] hover:bg-[#F5F9F2]"
                >
                  <CopyPlus className="w-4 h-4 mr-2" />
                  {duplicateLoading ? 'Se duplică...' : 'Duplică ziua precedentă'}
                </Button>
              </div>
            </div>

            {menuItems.length === 0 ? (
              <div className="text-center py-12 minimalist-card p-8">
                <p className="text-[#999]\">Nu există produse pentru această zi</p>
              </div>
            ) : (
              <div className="minimalist-card overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produs</TableHead>
                      <TableHead>Categorie</TableHead>
                      <TableHead>Preț</TableHead>
                      <TableHead>Stoc</TableHead>
                      <TableHead>Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {menuItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <div>{item.name}</div>
                          {item.description && (
                            <div className="text-xs text-[#666] mt-0.5 font-normal">{item.description}</div>
                          )}
                        </TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell>{item.price.toFixed(2)} RON</TableCell>
                        <TableCell>
                          {item.stock === null || item.stock === undefined ? (
                            <span className="text-sm font-medium text-[#5C8945]" title="Stoc toată ziua">∞ toată ziua</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                data-testid={`stock-decrease-${item.id}`}
                                onClick={() => updateStock(item.id, Math.max(0, item.stock - 1))}
                                className="quantity-btn"
                              >
                                −
                              </button>
                              <span className="w-12 text-center font-semibold">{item.stock}</span>
                              <button
                                data-testid={`stock-increase-${item.id}`}
                                onClick={() => updateStock(item.id, item.stock + 1)}
                                className="quantity-btn"
                              >
                                +
                              </button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <button
                            data-testid={`delete-item-${item.id}`}
                            onClick={() => deleteMenuItem(item.id)}
                            className="p-2 rounded-lg text-[#E54B4B] hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div data-testid="products-section">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
              <div className="relative max-w-md w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#999]" />
                <Input
                  data-testid="products-search-input"
                  value={productsSearch}
                  onChange={(e) => setProductsSearch(e.target.value)}
                  placeholder="Caută produs după nume..."
                  className="rounded-lg pl-10"
                />
              </div>
              <Button
                data-testid="products-new-button"
                onClick={() => setShowProductDialog(true)}
                className="rounded-lg bg-[#5C9F5F] hover:bg-[#4A8F4D] text-white"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Produs Nou
              </Button>
            </div>

            {products.filter(p => !productsSearch || p.name.toLowerCase().includes(productsSearch.toLowerCase())).length === 0 ? (
              <div className="text-center py-12 minimalist-card p-8">
                <p className="text-[#999]">Nu există produse în baza de date</p>
              </div>
            ) : (
              <div className="minimalist-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nume</TableHead>
                      <TableHead>Descriere</TableHead>
                      <TableHead>Categorie</TableHead>
                      <TableHead>Preț</TableHead>
                      <TableHead className="text-right">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products
                      .filter(p => !productsSearch || p.name.toLowerCase().includes(productsSearch.toLowerCase()))
                      .map((product) => {
                        const isEditing = editingProduct?.id === product.id;
                        return (
                          <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                            <TableCell className="font-medium align-top">
                              {isEditing ? (
                                <Input
                                  data-testid={`edit-product-name-${product.id}`}
                                  value={editingProduct.name}
                                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                                  className="rounded-md"
                                />
                              ) : product.name}
                            </TableCell>
                            <TableCell className="text-sm text-[#666] align-top max-w-xs">
                              {isEditing ? (
                                <Input
                                  data-testid={`edit-product-description-${product.id}`}
                                  value={editingProduct.description || ''}
                                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                                  placeholder="Descriere..."
                                  className="rounded-md"
                                />
                              ) : (product.description || <span className="text-[#CBD5E0] italic">—</span>)}
                            </TableCell>
                            <TableCell className="align-top">
                              {isEditing ? (
                                <Select
                                  value={editingProduct.category}
                                  onValueChange={(value) => setEditingProduct({ ...editingProduct, category: value })}
                                >
                                  <SelectTrigger data-testid={`edit-product-category-${product.id}`} className="rounded-md w-40">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {categories.map((cat) => (
                                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : product.category}
                            </TableCell>
                            <TableCell className="align-top">
                              {isEditing ? (
                                <Input
                                  data-testid={`edit-product-price-${product.id}`}
                                  type="number"
                                  step="0.01"
                                  value={editingProduct.price}
                                  onChange={(e) => setEditingProduct({ ...editingProduct, price: e.target.value })}
                                  className="rounded-md w-24"
                                />
                              ) : `${product.price.toFixed(2)} RON`}
                            </TableCell>
                            <TableCell className="text-right align-top">
                              <div className="flex gap-2 justify-end">
                                {isEditing ? (
                                  <>
                                    <Button
                                      data-testid={`save-product-${product.id}`}
                                      onClick={updateProduct}
                                      size="sm"
                                      className="rounded-md bg-[#5C9F5F] hover:bg-[#4A8F4D] text-white"
                                    >
                                      <Save className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      data-testid={`cancel-edit-${product.id}`}
                                      onClick={() => setEditingProduct(null)}
                                      size="sm"
                                      variant="outline"
                                      className="rounded-md"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      data-testid={`edit-product-full-${product.id}`}
                                      onClick={() => openEditProductDialog(product)}
                                      size="sm"
                                      variant="outline"
                                      className="rounded-md"
                                      title="Editare completă (inclusiv opțiuni combo)"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      data-testid={`delete-product-${product.id}`}
                                      onClick={() => deleteProduct(product.id)}
                                      size="sm"
                                      variant="outline"
                                      className="rounded-md text-red-600 border-red-200 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            {/* Search bar + sort toggle */}
            <div className="minimalist-card p-4 mb-4">
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
                  <input
                    data-testid="orders-search-input"
                    type="text"
                    placeholder="Caută după nume sau telefon..."
                    value={ordersSearch}
                    onChange={(e) => setOrdersSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-[#E8E8E8] rounded-lg focus:outline-none focus:border-[#7BA05B] bg-white"
                  />
                  {ordersSearch && (
                    <button
                      data-testid="orders-search-clear"
                      onClick={() => setOrdersSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#666]"
                    >
                      ×
                    </button>
                  )}
                </div>
                {/* Sort toggle */}
                <button
                  data-testid="orders-sort-toggle"
                  onClick={() => setOrderSortOrder(o => o === 'newest' ? 'oldest' : 'newest')}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-[#E8E8E8] bg-white hover:border-[#7BA05B] hover:text-[#5C8945] transition-colors whitespace-nowrap"
                  title="Schimbă ordinea de sortare"
                >
                  {orderSortOrder === 'newest' ? (
                    <><ArrowDownNarrowWide className="w-3.5 h-3.5" /> Cele mai noi</>
                  ) : (
                    <><ArrowUpNarrowWide className="w-3.5 h-3.5" /> Cele mai vechi</>
                  )}
                </button>
              </div>
              {ordersSearch && (
                <p className="text-xs text-[#999] mt-2">
                  {orders.filter(o =>
                    (o.customer_name ?? '').toLowerCase().includes(ordersSearch.toLowerCase()) ||
                    (o.customer_phone ?? '').includes(ordersSearch)
                  ).length} rezultate pentru „{ordersSearch}"
                </p>
              )}
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-12 minimalist-card p-8">
                <p className="text-[#999]\">Nu există comenzi</p>
              </div>
            ) : (
              <div className="minimalist-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Telefon</TableHead>
                      <TableHead>Produse</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Data ridicare</TableHead>
                      <TableHead>Interval</TableHead>
                      <TableHead>Plată</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders
                      .filter(o => !ordersSearch ||
                        (o.customer_name ?? '').toLowerCase().includes(ordersSearch.toLowerCase()) ||
                        (o.customer_phone ?? '').includes(ordersSearch)
                      )
                      .sort((a, b) => {
                        const aTs = a.created_at ? new Date(a.created_at).getTime() : 0;
                        const bTs = b.created_at ? new Date(b.created_at).getTime() : 0;
                        return orderSortOrder === 'newest' ? bTs - aTs : aTs - bTs;
                      })
                      .map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1.5">
                            {order.customer_name || '—'}
                            {order.delivery_type === 'delivery' && (
                              <span className="text-[10px] font-semibold bg-[#EEF2FF] text-[#4F46E5] px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`delivery-badge-${order.id}`}>
                                Livrare
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{order.customer_phone || '—'}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {(order.items || []).map((item, idx) => (
                              <div key={idx}>{item.quantity}x {item.name}</div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">{(order.total_amount ?? 0).toFixed(2)} RON</TableCell>
                        <TableCell data-testid={`order-date-${order.id}`}>
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#2D3748]">
                            <CalendarDays className="w-3.5 h-3.5 text-[#7BA05B]" />
                            {formatOrderDateLabel(order.order_date)}
                          </span>
                        </TableCell>
                        <TableCell data-testid={`order-pickup-${order.id}`}>{order.pickup_time || '—'}</TableCell>
                        <TableCell>
                          <span className="text-xs px-2 py-1 rounded-full bg-[#F8F8F8] text-[#666]">
                            {order.payment_method}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select
                              value={order.status}
                              onValueChange={(value) => {
                                if (value === 'intarziere') {
                                  applyDelay(order.id);
                                } else {
                                  updateOrderStatus(order.id, value);
                                }
                              }}
                            >
                              <SelectTrigger data-testid={`order-status-${order.id}`} className="w-36 rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Nouă">Nouă</SelectItem>
                                <SelectItem value="În pregătire">În pregătire</SelectItem>
                                <SelectItem value="intarziere">Întârziere...</SelectItem>
                                <SelectItem value="Finalizată">Finalizată</SelectItem>
                              </SelectContent>
                            </Select>
                            {order.delay_minutes > 0 && order.status === 'intarziere' && (
                              <span className="text-xs bg-[#FFF7E6] text-[#8B5A00] px-2 py-1 rounded-full font-semibold" data-testid={`order-delay-${order.id}`}>
                                +{order.delay_minutes} min
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            data-testid={`order-detail-button-${order.id}`}
                            onClick={() => setOrderDetail(order)}
                            size="sm"
                            variant="outline"
                            className="rounded-md"
                            title="Detalii comandă"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {/* Order detail dialog — outside the orders tab block so it opens correctly from
            any context (e.g., when clicking "Confirmă" in the new order alert modal) */}
        <Dialog open={!!orderDetail} onOpenChange={(open) => !open && setOrderDetail(null)}>
          <DialogContent className="bg-white max-w-lg" data-testid="order-detail-dialog">
            {orderDetail && (
              <>
                <DialogHeader>
                  <DialogTitle>Detalii comandă — {orderDetail.customer_name}</DialogTitle>
                  <DialogDescription>
                    Data ridicare {formatOrderDateLabel(orderDetail.order_date)}, interval {orderDetail.pickup_time || 'nespecificat'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[#999] text-xs uppercase font-semibold mb-0.5">Telefon</p>
                      <p className="font-medium text-[#1F1F1F]">{orderDetail.customer_phone}</p>
                    </div>
                    <div>
                      <p className="text-[#999] text-xs uppercase font-semibold mb-0.5">Email</p>
                      <p className="font-medium text-[#1F1F1F]">{orderDetail.customer_email || '—'}</p>
                    </div>
                  </div>

                  <div className="bg-[#F5F9F1] border border-[#7BA05B]/30 rounded-lg p-3" data-testid="order-detail-pickup">
                    <p className="text-sm font-semibold text-[#2D3748]">
                      Data ridicare: <span className="text-[#5C8945]">{formatOrderDateLabel(orderDetail.order_date)}</span>
                    </p>
                    <p className="text-sm font-semibold text-[#2D3748] mt-1">
                      Interval: <span className="text-[#5C8945]">{orderDetail.pickup_time || 'Nespecificat'}</span>
                    </p>
                  </div>

                  {orderDetail.delivery_type === 'delivery' && orderDetail.delivery_details && (
                    <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-3" data-testid="order-detail-delivery">
                      <p className="text-xs font-semibold text-[#4F46E5] uppercase mb-2">Detalii livrare</p>
                      <div className="space-y-1 text-sm text-[#2D3748]">
                        {orderDetail.delivery_details.company && (
                          <p><span className="text-[#718096]">Companie / Clădire:</span> <span className="font-medium">{orderDetail.delivery_details.company}</span></p>
                        )}
                        {orderDetail.delivery_details.address && (
                          <p><span className="text-[#718096]">Adresă:</span> <span className="font-medium">{orderDetail.delivery_details.address}</span></p>
                        )}
                        {orderDetail.delivery_details.floor && (
                          <p><span className="text-[#718096]">Etaj / Birou:</span> <span className="font-medium">{orderDetail.delivery_details.floor}</span></p>
                        )}
                        {orderDetail.delivery_details.instructions && (
                          <p><span className="text-[#718096]">Instrucțiuni:</span> <span className="font-medium">{orderDetail.delivery_details.instructions}</span></p>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-[#999] text-xs uppercase font-semibold mb-2">Produse</p>
                    <div className="space-y-2">
                      {(orderDetail.items || []).map((item, idx) => (
                        <div key={idx} className="text-sm border-b border-[#F0F0F0] pb-2 last:border-0">
                          <div className="flex justify-between font-medium text-[#1F1F1F]">
                            <span>{item.quantity}x {item.name}</span>
                            <span>{((item.price ?? 0) * (item.quantity ?? 1)).toFixed(2)} RON</span>
                          </div>
                          {(item.choices || []).length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {item.choices.map((c, ci) => (
                                <li key={ci} className="text-xs text-[#718096]">
                                  <span className="font-medium text-[#5C8945]">{c.group_title}:</span>{' '}
                                  {(c.selections || []).map(s => s.name).join(', ')}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t-2 border-[#E8E8E8]">
                    <span className="font-semibold text-[#1F1F1F]">Total</span>
                    <span className="text-lg font-bold text-[#7BA05B]">{(orderDetail.total_amount ?? 0).toFixed(2)} RON</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#999]">Plată: <span className="font-medium text-[#1F1F1F]">{orderDetail.payment_method}</span></span>
                    <span className="text-[#999]">Status: <span className="font-medium text-[#1F1F1F]">{orderDetail.status}</span></span>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {activeTab === 'reports' && (
          <div>
            <div className="flex gap-2 mb-6">
              {daysOfWeek.map((day) => (
                <button
                  key={day.value}
                  onClick={() => setSelectedDay(day.value)}
                  className={`category-tab ${selectedDay === day.value ? 'active' : ''}`}
                >
                  {day.label}
                </button>
              ))}
            </div>

            {!dailyReport ? (
              <div className="text-center py-12 minimalist-card p-8">
                <p className="text-[#999]">Se încarcă raportul...</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="minimalist-card p-5">
                    <p className="text-sm text-[#666] mb-1">Vânzări Totale</p>
                    <p className="text-3xl font-bold text-[#7BA05B]">{dailyReport.totalRevenue.toFixed(2)} RON</p>
                  </div>
                  <div className="minimalist-card p-5">
                    <p className="text-sm text-[#666] mb-1">Număr Comenzi</p>
                    <p className="text-3xl font-bold text-[#5C9F5F]">{dailyReport.totalOrders}</p>
                  </div>
                  <div className="minimalist-card p-5">
                    <p className="text-sm text-[#666] mb-1">Valoare Medie</p>
                    <p className="text-3xl font-bold text-[#1F1F1F]">
                      {dailyReport.totalOrders > 0 ? (dailyReport.totalRevenue / dailyReport.totalOrders).toFixed(2) : '0.00'} RON
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="minimalist-card p-5">
                    <h3 className="text-lg font-semibold text-[#1F1F1F] mb-4">Vânzări pe Categorie</h3>
                    {Object.keys(dailyReport.revenueByCategory).length === 0 ? (
                      <p className="text-[#999] text-center py-4">Nu există date</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(dailyReport.revenueByCategory)
                          .sort((a, b) => b[1] - a[1])
                          .map(([category, revenue]) => (
                            <div key={category} className="flex justify-between items-center">
                              <span className="text-[#666]">{category}</span>
                              <span className="font-semibold text-[#1F1F1F]">{revenue.toFixed(2)} RON</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="minimalist-card p-5">
                    <h3 className="text-lg font-semibold text-[#1F1F1F] mb-4">Metode de Plată</h3>
                    <div className="space-y-3">
                      {Object.entries(dailyReport.ordersByPaymentMethod).map(([method, count]) => (
                        <div key={method} className="flex justify-between items-center">
                          <span className="text-[#666]">{method}</span>
                          <span className="font-semibold text-[#1F1F1F]">{count} comenzi</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="minimalist-card p-5">
                  <h3 className="text-lg font-semibold text-[#1F1F1F] mb-4">Produse Cele Mai Vândute</h3>
                  {dailyReport.itemsSold.length === 0 ? (
                    <p className="text-[#999] text-center py-4">Nu există vânzări</p>
                  ) : (
                    <div className="minimalist-card overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produs</TableHead>
                            <TableHead>Categorie</TableHead>
                            <TableHead>Cantitate Vândută</TableHead>
                            <TableHead>Venituri</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyReport.itemsSold
                            .sort((a, b) => b.quantity - a.quantity)
                            .map((item) => (
                              <TableRow key={item.name}>
                                <TableCell className="font-medium">{item.name}</TableCell>
                                <TableCell>{item.category}</TableCell>
                                <TableCell>{item.quantity} porții</TableCell>
                                <TableCell className="font-semibold">{item.revenue.toFixed(2)} RON</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                <div className="minimalist-card p-5">
                  <h3 className="text-lg font-semibold text-[#1F1F1F] mb-4">Status Inventar</h3>
                  {dailyReport.stockStatus.length === 0 ? (
                    <p className="text-[#999] text-center py-4">Nu există produse în meniu</p>
                  ) : (
                    <div className="minimalist-card overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produs</TableHead>
                            <TableHead>Categorie</TableHead>
                            <TableHead>Stoc Inițial</TableHead>
                            <TableHead>Vândut</TableHead>
                            <TableHead>Rămas</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dailyReport.stockStatus.map((item) => (
                            <TableRow key={item.name}>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell>{item.category}</TableCell>
                              <TableCell>{item.initialStock}</TableCell>
                              <TableCell className="text-[#5C9F5F]">{item.sold}</TableCell>
                              <TableCell className={item.remaining === 0 ? 'text-[#E54B4B] font-semibold' : ''}>
                                {item.remaining}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}


        {activeTab === 'pending' && (
          <div className="max-w-5xl">
            <div className="minimalist-card p-6">
              <h2 className="text-xl font-semibold text-[#1F1F1F] mb-2">Plăți inițiate fără confirmare</h2>
              <p className="text-sm text-[#666] mb-4">
                Tranzacții pentru care clientul a inițiat plata Stripe, dar nu am primit confirmarea (webhook).
                Verifică în panoul Stripe (Dashboard &rarr; Payments) dacă plata a reușit. Dacă da, apasă <strong>Marchează plătită</strong> — comanda va fi creată automat.
              </p>
              {pendingPayments.length === 0 ? (
                <p className="text-sm text-[#999] italic">Nu există plăți în așteptare.</p>
              ) : (
                <div className="space-y-3">
                  {pendingPayments.map((p) => (
                    <div
                      key={p.session_id}
                      data-testid={`pending-payment-${p.session_id}`}
                      className="border border-[#E8E8E8] rounded-lg p-4 bg-[#FAFAF8]"
                    >
                      <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#1F1F1F]">{p.customer_name || '(fără nume)'}</p>
                          <p className="text-xs text-[#666]">
                            {p.customer_phone} · {p.customer_email || 'fără email'}
                          </p>
                          <p className="text-xs text-[#666] mt-1">
                            Ridicare: <span className="font-medium">{p.pickup_time || '—'}</span> · {p.order_date} · {p.items_count} produs(e)
                          </p>
                          <p className="text-xs text-[#999] font-mono mt-1">{p.session_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#7BA05B]">{p.amount?.toFixed(2)} {p.currency}</p>
                          <p className="text-xs text-[#999]">{new Date(p.created_at).toLocaleString('ro-RO')}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          data-testid={`mark-paid-${p.session_id}`}
                          onClick={() => handleMarkPaid(p.session_id)}
                          className="bg-[#7BA05B] hover:bg-[#5C8945] text-white"
                          size="sm"
                        >
                          Marchează plătită
                        </Button>
                        <Button
                          data-testid={`delete-pending-${p.session_id}`}
                          onClick={() => handleDeletePending(p.session_id)}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          size="sm"
                        >
                          Șterge
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        {activeTab === 'settings' && (
          <div className="max-w-3xl space-y-6">
            <div className="minimalist-card p-6" data-testid="slot-capacity-section">
              <h2 className="text-xl font-semibold text-[#1F1F1F] mb-2">Capacități Intervale Ridicare</h2>
              <p className="text-sm text-[#666] mb-4">
                Numărul maxim de comenzi pentru fiecare interval de 30 min. Când e atins, intervalul devine &quot;SOLD OUT&quot; pe pagina clientului. Intervalele curente se activează automat la 00:00 și se închid cu 60 min înainte de start.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {slotConfig.map(s => (
                  <div key={s.slot} className="flex items-center justify-between gap-3 p-3 bg-[#FAFAF8] border border-[#E8E8E8] rounded-lg">
                    <span className="font-mono font-semibold text-[#1F1F1F]">{s.slot}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        data-testid={`slot-capacity-${s.slot}`}
                        value={s.capacity}
                        onChange={(e) => updateSlotCapacity(s.slot, e.target.value)}
                        className="w-20 rounded-md text-center"
                      />
                      <span className="text-xs text-[#999]">comenzi</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="minimalist-card p-6">
              <h2 className="text-xl font-semibold text-[#1F1F1F] mb-6">Setări Aplicație</h2>
              
              <div className="space-y-5">
                <div>
                  <Label htmlFor="iban" className="text-[#1F1F1F] mb-2 block font-medium">
                    IBAN Wise *
                  </Label>
                  <Input
                    data-testid="settings-iban-input"
                    id="iban"
                    value={settings.iban}
                    onChange={(e) => {
                      setSettings({ ...settings, iban: e.target.value });
                      setSettingsChanged(true);
                    }}
                    placeholder="RO49AAAA1B31007593840000"
                    className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B] font-mono"
                  />
                  <p className="text-sm text-[#666] mt-1">
                    Acest IBAN va fi afișat clienților care aleg &quot;Transfer Bancar&quot;
                  </p>
                </div>

                <div>
                  <Label htmlFor="company" className="text-[#1F1F1F] mb-2 block font-medium">
                    Nume Beneficiar *
                  </Label>
                  <Input
                    data-testid="settings-company-input"
                    id="company"
                    value={settings.company_name}
                    onChange={(e) => {
                      setSettings({ ...settings, company_name: e.target.value });
                      setSettingsChanged(true);
                    }}
                    placeholder="Cantina Noastră SRL"
                    className="rounded-lg border-[#E8E8E8] focus:border-[#7BA05B]"
                  />
                  <p className="text-sm text-[#666] mt-1">
                    Numele companiei care va apărea pe pagina de confirmare
                  </p>
                </div>

                <div>
                  <Label htmlFor="important-info" className="text-[#1F1F1F] mb-2 block font-medium">
                    Informații Importante (opțional)
                  </Label>
                  <textarea
                    data-testid="settings-info-textarea"
                    id="important-info"
                    value={settings.important_info}
                    onChange={(e) => {
                      setSettings({ ...settings, important_info: e.target.value });
                      setSettingsChanged(true);
                    }}
                    placeholder="Ex: Program cantin ă: Luni-Vineri 11:00-15:00&#10;Comenzile se ridică de la ghișeul 2&#10;Pentru comenzi speciale sunați la 0721..."
                    rows={5}
                    className="w-full rounded-lg border border-[#E8E8E8] focus:border-[#7BA05B] p-3 text-[#1F1F1F] resize-none"
                    style={{ outline: 'none' }}
                  />
                  <p className="text-sm text-[#666] mt-1">
                    Informații care vor fi afișate pe pagina de confirmare (program, locație, contact etc.)
                  </p>
                </div>

                <div className="pt-4 border-t border-[#E8E8E8]">
                  <Button
                    data-testid="save-settings-button"
                    onClick={saveSettings}
                    disabled={!settingsChanged}
                    className={`w-full py-3 rounded-lg font-medium transition-all ${
                      settingsChanged 
                        ? 'bg-[#7BA05B] hover:bg-[#5C8945] text-white' 
                        : 'bg-[#E8E8E8] text-[#999] cursor-not-allowed'
                    }`}
                  >
                    {settingsChanged ? 'Salvează Modificările' : 'Nicio Modificare'}
                  </Button>
                </div>
              </div>
            </div>

            <div className="minimalist-card p-5 mt-6 bg-[#FFF9F5] border-[#FFE8D9]">
              <h3 className="text-base font-semibold text-[#1F1F1F] mb-2">ℹ️ Notă Importantă</h3>
              <p className="text-sm text-[#666]">
                Modificările aduse aici vor fi reflectate imediat pe site-ul client. Asigură-te că datele introduse sunt corecte înainte de a salva.
              </p>
            </div>
          </div>
        )}


      </main>

      {/* Global product create/edit dialog — opens from Menu tab "Produs Nou" or Products tab "Edit" */}
      <Dialog open={showProductDialog} onOpenChange={(open) => {
        setShowProductDialog(open);
        if (!open) setNewProduct({ name: '', description: '', price: '', category: 'Specialități', is_combo: false, recurring: false, default_stock: '', option_groups: [] });
        if (!open) setAutoGenerateImage(false);
      }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{newProduct._editingId ? 'Editează Produs' : 'Creează Produs Nou'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nume</Label>
              <Input
                data-testid="product-name-input"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder="Ex: Meniu Snițel"
                className="rounded-lg"
              />
            </div>
            <div>
              <Label>Descriere (opțional)</Label>
              <Input
                data-testid="product-description-input"
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder="Ex: Garnitură inclusă"
                className="rounded-lg"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Preț (RON)</Label>
                <Input
                  data-testid="product-price-input"
                  type="number"
                  step="0.01"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  placeholder="Ex: 25"
                  className="rounded-lg"
                />
              </div>
              <div>
                <Label>Categorie</Label>
                <Select
                  value={newProduct.category}
                  onValueChange={(value) => setNewProduct({ ...newProduct, category: value })}
                >
                  <SelectTrigger data-testid="product-category-select" className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-6 pt-2 border-t border-[#F0F0F0]">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="product-is-combo"
                  checked={!!newProduct.is_combo}
                  onChange={(e) => setNewProduct({ ...newProduct, is_combo: e.target.checked })}
                />
                <span className="font-medium">Meniu Combo (cu opțiuni)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="product-recurring"
                  checked={!!newProduct.recurring}
                  onChange={(e) => setNewProduct({ ...newProduct, recurring: e.target.checked })}
                />
                <span className="font-medium">Apare automat Luni-Vineri</span>
              </label>
            </div>
            <div>
              <Label className="text-xs text-[#666]">Stoc implicit (opțional, lasă gol pentru &quot;toată ziua&quot;)</Label>
              <Input
                data-testid="product-default-stock"
                type="number"
                value={newProduct.default_stock}
                onChange={(e) => setNewProduct({ ...newProduct, default_stock: e.target.value })}
                placeholder="Lasă gol = stoc nelimitat"
                className="rounded-lg"
              />
            </div>

            {/* AI Illustration Section */}
            <div className="space-y-3 pt-3 border-t border-[#F0F0F0]">
              <h4 className="font-semibold text-[#1F1F1F] flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-[#7BA05B]" />
                Ilustrație produs
              </h4>

              {/* Show existing image if editing */}
              {newProduct._editingId && newProduct._hasImage && (
                <div className="flex items-center gap-3 p-3 bg-[#F5F5F0] rounded-lg">
                  <img
                    src={`${API}/product-image/${newProduct._editingId}`}
                    alt="Ilustrație curentă"
                    className="w-16 h-16 rounded-lg object-cover border border-[#E8E8E8]"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1F1F1F]">Ilustrație existentă</p>
                    <button
                      type="button"
                      onClick={async () => {
                        await axios.delete(`${API}/products/${newProduct._editingId}/image`, { withCredentials: true });
                        setNewProduct(prev => ({ ...prev, _hasImage: false }));
                        fetchProducts();
                        toast.success('Imaginea a fost ștearsă');
                      }}
                      className="text-xs text-red-500 hover:underline mt-0.5"
                    >
                      Șterge imaginea
                    </button>
                  </div>
                </div>
              )}

              {/* Generating spinner */}
              {newProduct._editingId && newProduct._imageGenerating && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  <span className="text-sm text-amber-700">Ilustrație în curs de generare...</span>
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="auto-generate-image-checkbox"
                  checked={autoGenerateImage}
                  onChange={e => setAutoGenerateImage(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#7BA05B]"
                />
                <div>
                  <p className="text-sm font-medium text-[#1F1F1F]">Generează automat pictograma</p>
                  <p className="text-xs text-[#999] mt-0.5">
                    AI-ul generează o ilustrație watercolor bazată pe numele introdus (30-60 sec)
                  </p>
                </div>
              </label>
            </div>
            {newProduct.is_combo && (
              <div className="space-y-3 pt-3 border-t border-[#F0F0F0]" data-testid="option-groups-section">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-[#1F1F1F]">Grupuri de Opțiuni</h4>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid="add-option-group"
                    onClick={() => setNewProduct({
                      ...newProduct,
                      option_groups: [...(newProduct.option_groups || []), { title: '', required: true, multi: false, options: [{ name: '', extra_price: 0 }] }]
                    })}
                  >
                    <PlusCircle className="w-4 h-4 mr-1" /> Adaugă Grup
                  </Button>
                </div>
                {(newProduct.option_groups || []).map((g, gi) => (
                  <div key={gi} className="border border-[#E8E8E8] rounded-lg p-3 bg-[#FAFAF8] space-y-2" data-testid={`option-group-${gi}`}>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Titlu grup (ex: Alege Felul de Bază)"
                        value={g.title}
                        onChange={(e) => {
                          const groups = [...newProduct.option_groups];
                          groups[gi] = { ...g, title: e.target.value };
                          setNewProduct({ ...newProduct, option_groups: groups });
                        }}
                        className="rounded-md text-sm"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const groups = newProduct.option_groups.filter((_, i) => i !== gi);
                          setNewProduct({ ...newProduct, option_groups: groups });
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={g.required !== false}
                          onChange={(e) => {
                            const groups = [...newProduct.option_groups];
                            groups[gi] = { ...g, required: e.target.checked };
                            setNewProduct({ ...newProduct, option_groups: groups });
                          }}
                        />
                        Obligatoriu
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!g.multi}
                          onChange={(e) => {
                            const groups = [...newProduct.option_groups];
                            groups[gi] = { ...g, multi: e.target.checked };
                            setNewProduct({ ...newProduct, option_groups: groups });
                          }}
                        />
                        Permite selecții multiple
                      </label>
                    </div>
                    <div className="space-y-1.5">
                      {(g.options || []).map((opt, oi) => (
                        <div key={oi} className="flex gap-2 items-center">
                          <Input
                            placeholder="Nume opțiune"
                            value={opt.name}
                            onChange={(e) => {
                              const groups = [...newProduct.option_groups];
                              const opts = [...groups[gi].options];
                              opts[oi] = { ...opt, name: e.target.value };
                              groups[gi] = { ...g, options: opts };
                              setNewProduct({ ...newProduct, option_groups: groups });
                            }}
                            className="rounded-md text-sm flex-1"
                          />
                          <Input
                            type="number"
                            step="0.5"
                            placeholder="+lei"
                            value={opt.extra_price}
                            onChange={(e) => {
                              const groups = [...newProduct.option_groups];
                              const opts = [...groups[gi].options];
                              opts[oi] = { ...opt, extra_price: e.target.value };
                              groups[gi] = { ...g, options: opts };
                              setNewProduct({ ...newProduct, option_groups: groups });
                            }}
                            className="rounded-md text-sm w-20"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const groups = [...newProduct.option_groups];
                              const opts = groups[gi].options.filter((_, i) => i !== oi);
                              groups[gi] = { ...g, options: opts };
                              setNewProduct({ ...newProduct, option_groups: groups });
                            }}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const groups = [...newProduct.option_groups];
                          groups[gi] = { ...g, options: [...(g.options || []), { name: '', extra_price: 0 }] };
                          setNewProduct({ ...newProduct, option_groups: groups });
                        }}
                        className="text-[#5C8945]"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Adaugă opțiune
                      </Button>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-[#999]">
                  Sugestie ordine grupuri: Alege Felul de Bază → Alege Garnitura → Alege Salata → Extra Sosuri (opțional, multiple) → Clătită (opțional, multiple).
                </p>
              </div>
            )}
            <Button
              data-testid="create-product-button"
              onClick={createProduct}
              className="w-full accent-button"
              disabled={!newProduct.name || !newProduct.price}
            >
              {newProduct._editingId ? 'Actualizează Produs' : 'Salvează Produs'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-white" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Schimbă Parola</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Parola Curentă</Label>
              <Input
                data-testid="current-password-input"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                placeholder="Introdu parola curentă"
                className="rounded-lg"
              />
            </div>
            <div>
              <Label>Parolă Nouă</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                placeholder="Minim 8 caractere"
                className="rounded-lg"
              />
            </div>
            <div>
              <Label>Confirmă Parola Nouă</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="Reintrodu parola nouă"
                className="rounded-lg"
              />
            </div>
            <Button
              data-testid="submit-change-password"
              onClick={handleChangePassword}
              disabled={!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
              className="w-full accent-button"
            >
              Salvează Parola Nouă
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
